import React from "react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, actions, breadcrumbBadge, emoji }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5 min-w-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5 min-w-0">
          {emoji && (
            <span
              className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-emerald-50 to-amber-50 border border-emerald-100/80 text-xl shadow-brand-sm shrink-0"
              aria-hidden
            >
              {emoji}
            </span>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h1
                className="text-[20px] sm:text-[24px] font-bold tracking-tight text-slate-900 truncate"
                data-testid="page-title"
              >
                {title}
              </h1>
              {breadcrumbBadge}
            </div>
            {subtitle && (
              <p className="text-[12.5px] sm:text-[13.5px] text-slate-500 mt-0.5 line-clamp-2 sm:line-clamp-none">
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto shrink-0">{actions}</div>
      )}
    </div>
  );
}

export function PageContainer({ children, className }) {
  return (
    <div
      className={cn("px-3 py-3 sm:px-4 sm:py-4 md:px-5 md:py-5 w-full max-w-none min-w-0 animate-fade-up", className)}
      data-testid="page-container"
    >
      {children}
    </div>
  );
}

export function Card({ children, className, padded = true, hover = false }) {
  return (
    <div
      className={cn(
        "bg-white/80 backdrop-blur-md border border-slate-200/90 rounded-2xl shadow-brand min-w-0",
        hover &&
          "transition-all duration-200 hover:-translate-y-0.5 hover:shadow-brand-lift hover:border-emerald-200/80",
        padded && "p-4 sm:p-5",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SectionTitle({ children, action, className, emoji }) {
  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-2 mb-3 min-w-0", className)}>
      <h2 className="text-[14.5px] font-semibold tracking-tight text-slate-900 flex items-center gap-1.5">
        {emoji && <span aria-hidden>{emoji}</span>}
        {children}
      </h2>
      {action}
    </div>
  );
}

export function Pill({ tone = "default", children, className }) {
  const tones = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    blue: "bg-emerald-50 text-emerald-800 border-emerald-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-rose-50 text-rose-700 border-rose-200",
    purple: "bg-amber-50 text-amber-800 border-amber-200",
    teal: "bg-emerald-50 text-emerald-800 border-emerald-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 h-5 rounded-md text-[11px] font-medium border whitespace-nowrap",
        tones[tone] || tones.default,
        className,
      )}
    >
      {children}
    </span>
  );
}

export function StatusDot({ tone = "default", className }) {
  const colors = {
    default: "bg-slate-400",
    green: "bg-emerald-500",
    red: "bg-rose-500",
    amber: "bg-amber-500",
    blue: "bg-emerald-500",
  };
  return <span className={cn("inline-block h-1.5 w-1.5 rounded-full", colors[tone], className)} />;
}

export function EmptyHint({ children }) {
  return (
    <div className="text-center text-[12.5px] text-slate-400 py-10 border border-dashed border-emerald-200/70 rounded-2xl bg-emerald-50/20">
      <div className="text-2xl mb-2" aria-hidden>
        📭
      </div>
      {children}
    </div>
  );
}
